// composables/useTextFormatter.ts

/**
 * Composable for text formatting in Nuxt 3
 * Transforms hyphenated strings into properly capitalized text
 *
 * @param {string | Ref<string>} text - The text to format (can be a ref or plain string)
 * @param {boolean} [capitalizeAll=true] - Whether to capitalize all words
 * @returns {ComputedRef<string>} A computed ref containing the formatted text
 *
 * @example
 * const title = ref('atelier-series')
 * const { formattedText } = useTextFormatter(title)
 * // formattedText.value === 'Atelier Series'
 */
export const useTextFormatter = (
  text: string | Ref<string>,
  capitalizeAll: boolean = true
) => {
  // Create computed property that will update whenever the input text changes
  const formattedText = computed(() => {
    // Handle both ref and plain string inputs

    const currentText = isRef(text) ? text.value : text;

    if (!currentText) return "";

    // Split the text by hyphens and filter out empty strings
    const words = currentText.split("-").filter((word) => word.length > 0);

    // Format each word
    const formattedWords = words.map((word, index) => {
      // If capitalizeAll is false, only capitalize the first word
      if (!capitalizeAll && index > 0) {
        return word?.toLowerCase();
      }

      // Handle single characters
      if (word.length <= 1) {
        return word.toUpperCase();
      }

      // Capitalize first letter and lowercase the rest
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    });

    // Join words with spaces
    return formattedWords.join(" ");
  });

  // Return computed and additional useful properties/methods
  return {
    formattedText,
    // Optional: Add raw formatting function for one-off uses
    format: (rawText: string) => {
      return rawText
        .split("-")
        .filter((word) => word.length > 0)
        .map((word, index) => {
          if (!capitalizeAll && index > 0) return word.toLowerCase();
          return word.length <= 1
            ? word.toUpperCase()
            : word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
        })
        .join(" ");
    },
  };
};
